import { useEffect, useState } from 'react'

const DISMISS_KEY     = 'pwa-install-dismissed'
const DISMISS_KEY_IOS = 'pwa-install-dismissed-ios'

// True for iPhone/iPad Safari (not Chrome on iOS, not Edge, not Firefox).
// Safari is the only iOS browser whose "Add to Home Screen" flow we can
// instruct users through - the others either lack PWA install or have a
// different UI we can't speak to.
// True when running inside the Median native app. The UA carries a "median"
// token and is available synchronously on first paint — unlike window.median,
// which the wrapper injects a few seconds late (so we can't rely on it when the
// banner mounts).
function inMedianApp() {
  if (typeof navigator === 'undefined') return false
  return /median|gonative/i.test(navigator.userAgent || '')
}

function isIosSafari() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (inMedianApp()) return false        // native wrapper, not Safari
  const isIos = /iPhone|iPad|iPod/.test(ua)
  if (!isIos) return false
  // iOS Chrome contains "CriOS", Firefox "FxiOS", Edge "EdgiOS"; only Safari
  // doesn't include any of those.
  return !/(CriOS|FxiOS|EdgiOS|OPiOS|Chrome)/.test(ua)
}

function inStandaloneMode() {
  if (typeof window === 'undefined') return false
  // Median native app wrapper — the user is already "installed", so never
  // show the "Add to Home Screen" prompt inside the app.
  if (inMedianApp()) return true
  if (typeof window.median !== 'undefined') return true
  // Android / Chrome
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true
  // iOS adds this on the navigator object
  if (typeof window.navigator.standalone === 'boolean' && window.navigator.standalone) return true
  return false
}

const s = {
  wrap: {
    position: 'fixed', left: 0, right: 0,
    bottom: 'calc(env(safe-area-inset-bottom) + 62px)', // sit above the bottom nav
    zIndex: 9999,
    display: 'flex', justifyContent: 'center', padding: '0 12px',
    pointerEvents: 'none',
    fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    pointerEvents: 'auto',
    width: '100%', maxWidth: 406,
    background: 'var(--white)', border: '1px solid var(--border)',
    borderRadius: 14, padding: '12px 14px',
    boxShadow: '0 12px 40px var(--shadow)',
    display: 'flex', alignItems: 'center', gap: 12,
  },
  text: {
    flex: 1, minWidth: 0,
    fontSize: 13, fontWeight: 600, color: 'var(--ink)',
    lineHeight: 1.4,
  },
  install: {
    background: 'var(--gold)', color: 'var(--ink)', border: 'none',
    borderRadius: 8, padding: '8px 14px',
    fontSize: 13, fontWeight: 800, cursor: 'pointer',
    fontFamily: 'inherit', flexShrink: 0,
  },
  dismiss: {
    background: 'transparent', color: 'var(--stone)',
    border: 'none', cursor: 'pointer', padding: 6,
    fontSize: 18, lineHeight: 1, fontFamily: 'inherit',
    flexShrink: 0,
  },
}

// Tiny inline SVG that mimics the iOS Safari Share glyph (square with arrow up).
const ShareIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true"
    style={{ verticalAlign: '-2px', margin: '0 2px' }}>
    <path d="M12 3l4 4m-4-4l-4 4m4-4v12" stroke="#7BB3E0" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" stroke="#7BB3E0"
      strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

export default function InstallBanner() {
  const [deferred,    setDeferred]    = useState(null)
  const [visible,     setVisible]     = useState(false)
  const [iosVisible,  setIosVisible]  = useState(false)

  useEffect(() => {
    if (inStandaloneMode()) return

    // iOS Safari path: there's no beforeinstallprompt - we show a hint banner
    // explaining the manual Share → "Add to Home Screen" flow, unless the
    // user has previously dismissed it.
    if (isIosSafari()) {
      if (!localStorage.getItem(DISMISS_KEY_IOS)) setIosVisible(true)
      return
    }

    // Everything else: listen for the Chromium install prompt.
    if (localStorage.getItem(DISMISS_KEY)) return

    function onPrompt(e) {
      e.preventDefault()
      setDeferred(e)
      setVisible(true)
    }
    function onInstalled() {
      setVisible(false)
      setDeferred(null)
      localStorage.setItem(DISMISS_KEY, 'installed')
    }

    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  async function handleInstall() {
    if (!deferred) return
    deferred.prompt()
    const { outcome } = await deferred.userChoice
    if (outcome === 'accepted') localStorage.setItem(DISMISS_KEY, 'installed')
    setDeferred(null)
    setVisible(false)
  }

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, '1')
    setVisible(false)
  }

  function handleDismissIos() {
    localStorage.setItem(DISMISS_KEY_IOS, '1')
    setIosVisible(false)
  }

  // ─── iOS Safari hint banner ──────────────────────────────────────────────
  if (iosVisible) {
    return (
      <div style={s.wrap} role="dialog" aria-label="Add to home screen">
        <div style={s.card}>
          <div style={s.text}>
            To install: tap the Share button <ShareIcon/> then 'Add to Homescreen'.
          </div>
          <button style={s.dismiss} onClick={handleDismissIos} aria-label="Dismiss">×</button>
        </div>
      </div>
    )
  }

  // ─── Chromium install-prompt banner ──────────────────────────────────────
  if (!visible) return null
  return (
    <div style={s.wrap} role="dialog" aria-label="Install app">
      <div style={s.card}>
        <div style={s.text}>
          Add to your home screen for the best experience
        </div>
        <button style={s.install} onClick={handleInstall}>Install</button>
        <button style={s.dismiss} onClick={handleDismiss} aria-label="Dismiss">×</button>
      </div>
    </div>
  )
}
